import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';
import { toast } from 'sonner';

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function ColorField({ label, desc, value, onChange, error }: {
  label: string; desc: string; value: string; onChange: (v: string) => void; error?: string;
}) {
  return (
    <div>
      <label className="text-sm font-medium text-white block mb-1">{label}</label>
      <p className="text-xs text-fieldiq-muted mb-2">{desc}</p>
      <div className="flex items-center gap-3">
        <div className="relative">
          <div className="w-10 h-10 rounded-lg border-2 border-fieldiq-border" style={{ backgroundColor: value }} />
          <input type="color" value={value} onChange={e => onChange(e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
        </div>
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          maxLength={7}
          className={`w-28 bg-fieldiq-dark border rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none ${
            error ? 'border-red-500 focus:border-red-500' : 'border-fieldiq-border focus:border-fieldiq-accent/50'
          }`}
          placeholder="#1a365d"
        />
        {error && <span className="text-xs text-red-400">{error}</span>}
      </div>
    </div>
  );
}

/*
 * Tenant brand colours: primary and accent. Where they actually land, per the
 * API: the field ticket, invoice and quote PDFs, and the emails FieldIQ sends
 * under the company's name. BOL and weight-ticket PDFs do not read them.
 *
 * The endpoint also accepts brand_secondary_color. It saves, and its only
 * reader is the white-label CSS endpoint, which this app never calls — so it is
 * not offered here, and not sent, so a save leaves the stored value alone.
 *
 * There is no dark-mode switch here on purpose. Dark mode is a per-browser
 * preference, not a company setting, and the global header's sun/moon button
 * already sets it for every user on every page.
 */
export default function AppearancePage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'appearance'],
    queryFn: () => apiClient.get('/settings/appearance').then(r => r.data?.data ?? r.data ?? {}),
  });

  const [form, setForm] = useState({
    brand_primary_color: '#1a365d',
    brand_accent_color: '#d4943a',
  });
  const [dirty, setDirty] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (data) {
      setForm({
        brand_primary_color: data.brand_primary_color || '#1a365d',
        brand_accent_color: data.brand_accent_color || '#d4943a',
      });
      setDirty(false);
    }
  }, [data]);

  const updateColor = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    if (value.length === 7 && !HEX_RE.test(value)) {
      setErrors(prev => ({ ...prev, [key]: 'Invalid hex' }));
    } else {
      setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    }
  };

  const hasErrors = Object.keys(errors).length > 0 ||
    !HEX_RE.test(form.brand_primary_color) ||
    !HEX_RE.test(form.brand_accent_color);

  const saveMutation = useMutation({
    mutationFn: (payload: any) => apiClient.patch('/settings/appearance', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'appearance'] });
      setDirty(false);
      toast.success('Appearance settings saved');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to save'),
  });

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  return (
    <div className="min-h-full">
      {/* The Organization workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-6">
        {/* Brand Colors */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-white">Brand Colors</h3>
            <p className="text-xs text-fieldiq-muted mt-0.5">
              Used on field ticket, invoice and quote PDFs, and on emails sent under your company name.
              BOLs and weight tickets are not branded.
            </p>
          </div>

          <ColorField label="Primary Color" desc="Used for headers and title text"
            value={form.brand_primary_color} onChange={v => updateColor('brand_primary_color', v)} error={errors.brand_primary_color} />
          <ColorField label="Accent Color" desc="Used for section headings and borders"
            value={form.brand_accent_color} onChange={v => updateColor('brand_accent_color', v)} error={errors.brand_accent_color} />
        </div>

        {/* Live Preview */}
        <div>
          <h3 className="text-sm font-semibold text-white mb-3">Preview</h3>
          <div className="bg-white rounded-xl overflow-hidden border border-fieldiq-border shadow-sm">
            {/* Mock invoice header */}
            <div className="px-5 py-4" style={{ backgroundColor: form.brand_primary_color }}>
              <p className="text-white font-bold text-base">Apex Hauling Ltd</p>
              <p className="text-white/70 text-xs mt-0.5">Invoice #INV-0042</p>
            </div>
            {/* Accent bar */}
            <div className="h-1" style={{ backgroundColor: form.brand_accent_color }} />
            {/* Body preview */}
            <div className="px-5 py-3 space-y-2">
              <p className="text-sm font-semibold" style={{ color: form.brand_accent_color }}>Bill To</p>
              <p className="text-xs text-gray-600">Customer Name Inc.</p>
              <p className="text-xs text-gray-600">Red Deer, AB &middot; April 26, 2026</p>
            </div>
            {/* Footer */}
            <div className="px-5 py-2 border-t border-gray-200 flex gap-3">
              <div className="flex-1 rounded p-2 text-center text-white text-[10px] font-bold" style={{ backgroundColor: form.brand_primary_color }}>Primary</div>
              <div className="flex-1 rounded p-2 text-center text-white text-[10px] font-bold" style={{ backgroundColor: form.brand_accent_color }}>Accent</div>
            </div>
          </div>
        </div>

        {/* Reset */}
        <button
          onClick={() => {
            updateColor('brand_primary_color', '#1a365d');
            updateColor('brand_accent_color', '#d4943a');
          }}
          className="text-sm text-fieldiq-accent hover:underline"
        >
          Reset to defaults
        </button>

        {/* Save */}
        <button
          onClick={() => saveMutation.mutate(form)}
          disabled={!dirty || hasErrors || saveMutation.isPending}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            dirty && !hasErrors ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
          }`}
        >
          {saveMutation.isPending ? <Loader2 size={16} className="animate-spin" /> : null}
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
